import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { setNotification } from '../../../redux/notificationSlice'
import { useRequest } from '../../../hooks/UseRequest'
import { buildQueryString } from '../../../helpers/formatting'
import {
  fieldErrorsFromValidation,
  withoutAfterPrefix,
} from './blockChangeHelpers'

const BASE = () =>
  `${process.env.REACT_APP_API_DOMAIN}/lead_funnel/block_change_requests`

// Every call resolves to the response body, or to null after the error has been shown.
// Reads and writes use separate useRequest instances so a write cannot clobber a read's endpoint mid-flight.
export function useBlockChangeRequestApi() {
  const dispatch = useDispatch()
  const navi = useNavigate()
  const readRequest = useRequest()
  const writeRequest = useRequest()

  // A refused write is reported where it happened; only a refused read means the page is off limits.
  // The permission middleware refuses with 401, so a 401 on a write is a denial too, not a lost session.
  const handleError = (error, fallback, isWrite, options) => {
    const { onFieldErrors, onConflict } = options ?? {}
    const status = error?.response?.status
    if ((status === 401 || status === 403) && !isWrite) {
      navi('/403')
      return null
    }
    if (status === 409 && onConflict) onConflict()
    if (status === 422 && onFieldErrors) {
      const fieldErrors = fieldErrorsFromValidation(error.response.data?.errors)
      if (Object.keys(fieldErrors).length > 0) onFieldErrors(fieldErrors)
    }
    dispatch(
      setNotification({
        type: 'error',
        body: withoutAfterPrefix(error?.response?.data?.message) ?? fallback,
      })
    )
    return null
  }

  const send = (request, method, path, body, fallback, options) => {
    request.setEndpoint(`${BASE()}${path}`)
    const isWrite = method !== 'get'
    const call = isWrite ? request[method](body ?? {}) : request.get()
    const fail = (error) => handleError(error, fallback, isWrite, options)
    return call.then((res) => res?.data ?? null).catch(fail)
  }

  return {
    list: ({ status, authorId, perPage, page }) =>
      send(
        readRequest,
        'get',
        buildQueryString({
          status,
          author_id: authorId,
          per_page: perPage,
          page,
        }),
        null,
        'Failed to load change requests'
      ),
    detail: (id) =>
      send(
        readRequest,
        'get',
        `/${id}`,
        null,
        'Failed to load the change request'
      ).then((body) => body?.data ?? null),
    create: ({ title, description }) =>
      send(
        writeRequest,
        'post',
        '',
        { title, description },
        'Failed to create the change request'
      ).then((body) => body?.data ?? null),
    action: (id, action, body = {}, { onConflict } = {}) =>
      send(
        writeRequest,
        'post',
        `/${id}/${action}`,
        body,
        'The action failed',
        { onConflict }
      ).then((response) => response?.data ?? null),
    addOperation: (id, operation, { onFieldErrors, onConflict } = {}) =>
      send(
        writeRequest,
        'post',
        `/${id}/operations`,
        operation,
        'Failed to add the change',
        { onFieldErrors, onConflict }
      ).then((body) => body?.data ?? null),
    // Sequences renumber on every edit, so the hash the tray was read under proves `sequence` still names the same change.
    removeOperation: (id, sequence, operationsHash, { onConflict } = {}) =>
      send(
        writeRequest,
        'delete',
        `/${id}/operations/${sequence}${buildQueryString({
          operations_hash: operationsHash,
        })}`,
        null,
        'Failed to remove the change',
        { onConflict }
      ).then((body) => body?.data ?? null),
  }
}
